/**
 * Release Notes Template Form
 * Version release documentation
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const ReleaseNotesForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    product_name: '', version: '', release_date: new Date().toISOString().split('T')[0],
    release_type: 'Minor', prepared_by: '', summary: '', highlights: [''],
    new_features: [{ feature_id: '', type: 'Feature', title: '', description: '', jira_ticket: '', developer: '' }],
    improvements: [], bug_fixes: [], breaking_changes: [], known_issues: [''], deprecations: [''],
    upgrade_instructions: '', rollback_instructions: '', dependencies: [''],
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    product_name: 'Platform V2',
    version: '2.5.0',
    release_date: new Date().toISOString().split('T')[0],
    release_type: 'Minor',
    prepared_by: 'Engineering Team',
    summary: 'This release introduces AI-powered search functionality, performance improvements, and several bug fixes. Users will notice faster page loads and more accurate search results.',
    highlights: [
      'New AI-powered search with natural language queries',
      '40% faster dashboard loading times',
      'Enhanced security with 2FA support',
      'Improved mobile responsiveness'
    ],
    new_features: [
      { feature_id: 'FEAT-123', type: 'Feature', title: 'AI-Powered Search', description: 'Natural language search across all data types with intelligent suggestions', jira_ticket: 'PLAT-456', developer: 'John Smith' },
      { feature_id: 'FEAT-124', type: 'Feature', title: 'Two-Factor Authentication', description: 'Optional 2FA support via authenticator apps', jira_ticket: 'PLAT-457', developer: 'Jane Doe' },
      { feature_id: 'FEAT-125', type: 'Feature', title: 'Bulk Export', description: 'Export up to 10,000 records at once in CSV or Excel format', jira_ticket: 'PLAT-458', developer: 'Mike Wilson' }
    ],
    improvements: [
      { feature_id: 'IMP-201', type: 'Enhancement', title: 'Dashboard Performance', description: 'Optimized queries reduce load time by 40%', jira_ticket: 'PLAT-459', developer: 'Sarah Chen' },
      { feature_id: 'IMP-202', type: 'Enhancement', title: 'Mobile UI', description: 'Improved responsive design for tablets and phones', jira_ticket: 'PLAT-460', developer: 'Alex Kumar' }
    ],
    bug_fixes: [
      { feature_id: 'BUG-301', type: 'Bug Fix', title: 'Date Picker Issue', description: 'Fixed date picker not working in Safari browser', jira_ticket: 'PLAT-461', developer: 'John Smith' },
      { feature_id: 'BUG-302', type: 'Bug Fix', title: 'Export Timeout', description: 'Fixed timeout on large data exports', jira_ticket: 'PLAT-462', developer: 'Mike Wilson' },
      { feature_id: 'BUG-303', type: 'Bug Fix', title: 'Login Session', description: 'Fixed premature session expiration issue', jira_ticket: 'PLAT-463', developer: 'Jane Doe' }
    ],
    breaking_changes: [
      { feature_id: 'BREAK-001', type: 'Breaking Change', title: 'API Endpoint Change', description: '/api/v1/users endpoint deprecated, use /api/v2/users', jira_ticket: 'PLAT-464', developer: 'Tech Lead' }
    ],
    known_issues: [
      'Charts may not render correctly in Internet Explorer 11 (EOL browser)',
      'Bulk export limited to 10,000 records per request'
    ],
    deprecations: [
      'Legacy authentication method will be removed in v3.0',
      '/api/v1/* endpoints deprecated, migrate to /api/v2/*'
    ],
    upgrade_instructions: '1. Backup your database\n2. Run database migrations: `npm run migrate`\n3. Clear browser cache\n4. Update API integrations to v2 endpoints',
    rollback_instructions: '1. Restore database from backup\n2. Deploy previous version tag\n3. Clear CDN cache',
    dependencies: ['Node.js 18+', 'PostgreSQL 14+', 'Redis 7+'],
    rai_assignments: [
      { task: 'Monitor release metrics for 24h', responsible: 'DevOps', accountable: 'Tech Lead', informed: 'Product, Support', due_date: new Date().toISOString().split('T')[0], status: 'Pending' }
    ],
    followups: [
      { item: 'Customer communication about breaking changes', owner: 'Product Manager', due_date: new Date().toISOString().split('T')[0], priority: 'High', status: 'Completed' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Release Notes Draft";
      const payload = {
        name: draftName,
        template_type: "Release Notes",
        template_id: "release-notes",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "release-notes", "Release Notes", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("release-notes", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/release-notes`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Release_Notes_${formData.product_name.replace(/\s+/g, '_')}_${formData.version}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Release Notes",
        formData,
        settings: activeSettings || {},
        templateType: "Release Notes",
        templateId: "release-notes",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addFeature = (type) => {
    const fieldMap = { 'new_features': 'FEAT', 'improvements': 'IMP', 'bug_fixes': 'BUG', 'breaking_changes': 'BREAK' };
    const typeMap = { 'new_features': 'Feature', 'improvements': 'Enhancement', 'bug_fixes': 'Bug Fix', 'breaking_changes': 'Breaking Change' };
    const prefix = fieldMap[type];
    const newId = `${prefix}-${String(formData[type].length + 1).padStart(3, '0')}`;
    setFormData({
      ...formData,
      [type]: [...formData[type], { feature_id: newId, type: typeMap[type], title: '', description: '', jira_ticket: '', developer: '' }]
    });
  };

  const updateFeature = (type, index, field, value) => {
    const items = [...formData[type]];
    items[index] = { ...items[index], [field]: value };
    setFormData({ ...formData, [type]: items });
  };

  const removeFeature = (type, index) => {
    setFormData({ ...formData, [type]: formData[type].filter((_, i) => i !== index) });
  };

  const releaseTypes = ['Major', 'Minor', 'Patch', 'Hotfix'];

  const renderFeatureSection = (type, label, bgColor) => (
    <div className={`p-4 rounded-lg ${bgColor}`}>
      <Label className="font-semibold">{label} ({formData[type].length})</Label>
      <div className="space-y-2 mt-2">
        {formData[type].map((item, idx) => (
          <div key={idx} className="grid grid-cols-1 md:grid-cols-6 gap-2 bg-white p-2 rounded border">
            <Input value={item.feature_id} onChange={(e) => updateFeature(type, idx, 'feature_id', e.target.value)} placeholder="ID" className="text-sm" />
            <Input value={item.title} onChange={(e) => updateFeature(type, idx, 'title', e.target.value)} placeholder="Title" className="text-sm" />
            <div className="md:col-span-2"><Input value={item.description} onChange={(e) => updateFeature(type, idx, 'description', e.target.value)} placeholder="Description" className="text-sm" /></div>
            <Input value={item.jira_ticket} onChange={(e) => updateFeature(type, idx, 'jira_ticket', e.target.value)} placeholder="Ticket" className="text-sm" />
            <div className="flex gap-1">
              <Input value={item.developer} onChange={(e) => updateFeature(type, idx, 'developer', e.target.value)} placeholder="Dev" className="text-sm" />
              <Button variant="ghost" size="icon" onClick={() => removeFeature(type, idx)} className="shrink-0"><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={() => addFeature(type)} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Release Notes</h1><p className="text-sm text-gray-500">Version release documentation</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Release Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="col-span-2"><Label>Product Name *</Label><Input value={formData.product_name} onChange={(e) => setFormData({...formData, product_name: e.target.value})} placeholder="Platform V2" /></div>
              <div><Label>Version *</Label><Input value={formData.version} onChange={(e) => setFormData({...formData, version: e.target.value})} placeholder="2.5.0" /></div>
              <div><Label>Release Date</Label><Input type="date" value={formData.release_date} onChange={(e) => setFormData({...formData, release_date: e.target.value})} /></div>
              <div><Label>Type</Label>
                <Select value={formData.release_type} onValueChange={(v) => setFormData({...formData, release_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{releaseTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div><Label>Summary</Label><Textarea value={formData.summary} onChange={(e) => setFormData({...formData, summary: e.target.value})} placeholder="Brief summary of this release..." rows={3} /></div>

            <div>
              <Label>Highlights</Label>
              {formData.highlights.map((h, idx) => (
                <div key={idx} className="flex gap-2 mt-1">
                  <Input value={h} onChange={(e) => { const hl = [...formData.highlights]; hl[idx] = e.target.value; setFormData({...formData, highlights: hl}); }} placeholder="Key highlight" />
                  <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, highlights: formData.highlights.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setFormData({...formData, highlights: [...formData.highlights, '']})} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>

            <Separator />

            {renderFeatureSection('new_features', 'New Features', 'bg-green-50')}
            {renderFeatureSection('improvements', 'Improvements', 'bg-blue-50')}
            {renderFeatureSection('bug_fixes', 'Bug Fixes', 'bg-yellow-50')}
            {renderFeatureSection('breaking_changes', 'Breaking Changes', 'bg-red-50')}

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Upgrade Instructions</Label><Textarea value={formData.upgrade_instructions} onChange={(e) => setFormData({...formData, upgrade_instructions: e.target.value})} placeholder="Steps to upgrade..." rows={4} /></div>
              <div><Label>Rollback Instructions</Label><Textarea value={formData.rollback_instructions} onChange={(e) => setFormData({...formData, rollback_instructions: e.target.value})} placeholder="How to rollback..." rows={4} /></div>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
              <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download DOCX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ReleaseNotesForm;
